import './poultryDesign.css';
import Button from '../shared/Button.js';
// ============================================================================
// src/broiler/BroilerHomeView.tsx  —  Phase 2 Round 6 (first extraction)
// ----------------------------------------------------------------------------
// First of the inline-JSX views lifted out of App's render body. Consumes
// context state directly via hooks; Header is a shared module component.
// ============================================================================
import React from 'react';
import {useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {openableProps} from '../shared/openable.js';
import {fmtDate as fmt, fmtDate as fmtS, todayISO, todayCentralISO} from '../lib/dateUtils.js';
import {
  calcTimeline,
  calcBatchFeed,
  calcBroilerStatsFromDailys,
  computeBroilerOnFarmCounts,
  getBatchDisplayColor,
} from '../lib/broiler.js';
import type {BroilerBatch, BroilerDailyRow} from '../lib/broiler.js';
import {worstBroilerWeighInFlag} from '../lib/broilerWeighIns.js';
import Badge from '../shared/Badge.tsx';
import {StatStrip, EquationStrip} from '../shared/StatStrip.js';
import {GroupDot} from '../shared/GroupPillPicker.js';
import {hatcheryComparison} from '../lib/broilerHatchery.js';
import {useUI} from '../contexts/UIContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {BroilerHomeCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';

// The ppp-v4 broiler batch row as THIS dashboard reads it. BatchesContext hands
// the rows out as `unknown[]`, so the narrowing happens here — at the one place
// that knows which columns it touches — on top of the open record
// src/lib/broiler.ts already declares for exactly these rows.
//
// WHY THE MONEY/FEED/COUNT COLUMNS ARE `number` AND NOT THE SHARED
// `number | string | null`: broiler.ts uses the wider union because every read
// in that module goes through parseFloat/parseInt, which tolerates a string.
// This dashboard does not. It adds and multiplies these columns directly —
// `(b.brooderFeedLbs || 0) * (b.perLbStarterCost || 0)`, `(b.brooderFeedLbs ||
// 0) + (b.schoonerFeedLbs || 0)`, `s + (b.totalToProcessor || 0)` — which is
// arithmetic only when both sides are numbers and would silently CONCATENATE on
// a stored string. Declaring `number` records the requirement the shipped code
// already has, and erases at build time. Wrapping those reads in `Number(...)`
// instead would be a real BEHAVIOUR change on string input (a `+` that
// concatenated would start adding), which is why it is not done at any of them.
//
// `Number(...)` IS used at the `>` and `/` sites, and only there, because those
// two operators already perform ToNumber on both operands — so the wrapper is
// erasure-only exactly where it appears.
//
// Columns this file only ever reads through parseFloat/parseInt keep the shared
// union.
//
// `name` is `string`, not the shared `string | null`, for the same reason: this
// file dereferences it directly (`b.name.toLowerCase()`), so a missing name
// already threw before any of this. `String(b.name)` would turn that crash into
// the literal text "null", which is a behaviour change; the declaration is not.
type BroilerBatchRecord = BroilerBatch & {
  name: string;
  schooner?: string | null;
  brooderFeedLbs?: number | null;
  schoonerFeedLbs?: number | null;
  gritLbs?: number | null;
  perLbStarterCost?: number | null;
  perLbStandardCost?: number | null;
  perLbGritCost?: number | null;
  processingCost?: number | null;
  mortalityCumulative?: number | null;
  totalToProcessor?: number | null;
  // Read only through parseFloat/parseInt or Number(), so the stored-string
  // tolerance the app_store blob really has stays visible.
  avgDressedLbs?: number | string | null;
  chickCost?: number | string | null;
  totalLbsWhole?: number | string | null;
  totalLbsCuts?: number | string | null;
};

// A batch proved to carry both dates, by the filter that immediately precedes
// every use. `.filter()` does not narrow on its own, so the predicate says so.
type DatedBatch = BroilerBatchRecord & {hatchDate: string; processingDate: string};
const hasBothDates = (b: BroilerBatchRecord): b is DatedBatch => Boolean(b.hatchDate && b.processingDate);

// A poultry_dailys row as this dashboard reads it: the columns
// calcBroilerStatsFromDailys already declares, plus `date`, which is NOT NULL on
// the table and is likewise declared `string` by BroilerDailysView.tsx.
type BroilerDailyRecord = BroilerDailyRow & {date: string};

// Frozen module constant, not an inline object literal: useRouteResource
// serializes params into its key, and a fresh {} every render would rebuild the
// key and re-run everything downstream of it forever.
const BROILER_HOME_PARAMS = Object.freeze({});

export default function BroilerHomeView() {
  const navigate = useNavigate();
  // Both inputs were App's former-eager boot results reached through provider
  // hooks — `batches` off BatchesContext, `broilerDailys` off
  // DailysRecentContext — and both are read through broiler.home.core now, so
  // `broilerHome` retires from the frozen app_store and poultry_dailys consumer
  // lists in tests/fixtures/former-eager-dependencies.json. Do not destructure
  // either off a provider hook again: that is one of the two channels
  // former_eager_evidence_static derives consumption from.
  const homeResource = useRouteResource(RESOURCE.BROILER_HOME_CORE, BROILER_HOME_PARAMS);
  const homeData = homeResource.data as BroilerHomeCoreData | null;
  const {setView} = useUI();
  // The one place this view states what it believes the two payload arrays
  // hold, as the provider hand-off used to. Asserting once here keeps the ~30
  // field reads below unannotated.
  const batches = (homeData?.batches || []) as BroilerBatchRecord[];
  const broilerDailys = (homeData?.broilerDailys || []) as BroilerDailyRecord[];
  const todayStr = todayISO();

  // Active batch tiles open the broiler batch record page. Mirrors the
  // canonical navigation used by BroilerListView/BroilerTimelineView:
  // /broiler/batches/<encodeURIComponent(name)>.
  function openBroilerBatch(b: BroilerBatchRecord) {
    if (b && b.name) navigate('/broiler/batches/' + encodeURIComponent(b.name));
  }
  const broilerOnFarmCounts = computeBroilerOnFarmCounts(batches, broilerDailys);
  // Same rows that went in — computeBroilerOnFarmCounts filters, it never
  // rebuilds — so they keep the record type this file narrowed them to.
  const activeBr = broilerOnFarmCounts.activeBatches as BroilerBatchRecord[];
  // Ignore batches before 2025 for all dashboard metrics
  const procBr = batches.filter((b) => b.status === 'processed' && (b.hatchDate || '') >= '2025');
  const currentYear = String(new Date().getFullYear());
  const procBrThisYear = procBr.filter((b) => (b.processingDate || '').startsWith(currentYear));
  const avg = <T,>(arr: T[], fn: (item: T) => number | null): number | null => {
    const v = arr.map(fn).filter((x): x is number => x != null && !isNaN(x));
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };

  // Get true feed/mort: daily reports for B-25+, manual fields for B-24
  // String(...) around a parseFloat/parseInt argument is erasure-only: both
  // already perform ToString on whatever they are handed.
  const batchFeed = (b: BroilerBatchRecord) => {
    if (/^b-24-/i.test(b.name)) return (b.brooderFeedLbs || 0) + (b.schoonerFeedLbs || 0);
    return broilerDailys
      .filter(
        (d) =>
          (d.batch_label || '')
            .toLowerCase()
            .trim()
            .replace(/^\(processed\)\s*/, '') === b.name.toLowerCase().trim(),
      )
      .reduce((s, d) => s + (parseFloat(String(d.feed_lbs)) || 0), 0);
  };
  const batchMort = (b: BroilerBatchRecord) => {
    if (/^b-24-/i.test(b.name)) return b.mortalityCumulative || 0;
    return broilerDailys
      .filter(
        (d) =>
          (d.batch_label || '')
            .toLowerCase()
            .trim()
            .replace(/^\(processed\)\s*/, '') === b.name.toLowerCase().trim(),
      )
      .reduce((s, d) => s + (parseInt(String(d.mortality_count)) || 0), 0);
  };

  // Hatchery comparison (handoff 2026-10): grouped from batches that already
  // name a hatchery; no new table, no new field.
  const hatcheryRows = hatcheryComparison(procBrThisYear, broilerDailys);

  // Active batch cards
  const activeBatchDetails = activeBr.map((b) => {
    const live = calcTimeline(b.hatchDate, b.breed, b.processingDate, b.growoutWeeks, b.brooderDays);
    const inBrooder = live && todayStr < live.brooderOut;
    const daysToProc = b.processingDate
      ? Math.max(0, Math.round((new Date(b.processingDate + 'T12:00:00').getTime() - new Date().getTime()) / 86400000))
      : null;
    const batchDailys = broilerDailys.filter(
      (d) =>
        (d.batch_label || '')
          .toLowerCase()
          .trim()
          .replace(/^\(processed\)\s*/, '') === b.name.toLowerCase().trim(),
    );
    const daysActive = b.hatchDate
      ? Math.max(1, Math.round((new Date().getTime() - new Date(b.hatchDate + 'T12:00:00').getTime()) / 86400000))
      : 1;
    const reportRate = Math.round((new Set(batchDailys.map((d) => d.date)).size / daysActive) * 100);
    const feedSoFar = batchDailys.reduce((s, d) => s + (parseFloat(String(d.feed_lbs)) || 0), 0);
    const gritSoFar = batchDailys.reduce((s, d) => s + (parseFloat(String(d.grit_lbs)) || 0), 0);
    const mortSoFar = batchDailys.reduce((s, d) => s + (parseInt(String(d.mortality_count)) || 0), 0);
    const mortPct = Number(b.birdCount) > 0 ? ((mortSoFar / Number(b.birdCount)) * 100).toFixed(1) : null;
    // The `hasBothDates` predicate carries the same two date checks the inline
    // filter made, so the callback below can read them as strings.
    const sameBreed = procBr.filter(
      (b2): b2 is DatedBatch =>
        b2.breed === b.breed && hasBothDates(b2) && (b2.brooderFeedLbs || 0) + (b2.schoonerFeedLbs || 0) > 0,
    );
    const histFeedDay =
      sameBreed.length >= 2
        ? avg(sameBreed, (b2) => {
            const tf = (b2.brooderFeedLbs || 0) + (b2.schoonerFeedLbs || 0);
            const d2 = Math.round(
              (new Date(b2.processingDate + 'T12:00:00').getTime() - new Date(b2.hatchDate + 'T12:00:00').getTime()) /
                86400000,
            );
            return d2 > 0 ? tf / d2 : null;
          })
        : null;
    const projFeed = histFeedDay ? Math.round(histFeedDay * daysActive) : null;
    const feedPct = projFeed && projFeed > 0 ? Math.round((feedSoFar / projFeed) * 100) : null;
    const phaseLabel = inBrooder
      ? live
        ? `Brooder · out ${fmtS(live.brooderOut)}`
        : 'Brooder'
      : live
        ? `Schooner · out ${fmtS(live.schoonerOut)}`
        : 'Schooner';
    return {b, inBrooder, daysToProc, daysActive, feedSoFar, mortSoFar, mortPct, phaseLabel};
  });

  // Last 10 processed for trends
  const trend10 = [...procBr]
    .sort((a, b) => (b.processingDate || '').localeCompare(a.processingDate || ''))
    .slice(0, 10)
    .reverse();

  const trendBar = (val: number, max: number, color: string) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4}}>
      <div
        style={{
          flex: 1,
          height: 7,
          background: '#f0f0f0',
          borderRadius: 4 /* radius-allow: 7px trend bar track */,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${Math.min(100, max > 0 ? (val / max) * 100 : 0)}%`,
            background: color,
            borderRadius: 4 /* radius-allow: 7px trend bar fill */,
          }}
        />
      </div>
      <span style={{fontSize: 10, color: 'var(--ink)', fontWeight: 600, minWidth: 36, textAlign: 'right'}}>
        {Math.round(val * 10) / 10}
      </span>
    </div>
  );

  // Financial — this year only
  const finBr = procBrThisYear.filter((b) => batchFeed(b) > 0 && Number(b.totalToProcessor) > 0);
  const totalLbsMeat = procBrThisYear.reduce(
    (s, b) => s + (b.totalToProcessor || 0) * (parseFloat(String(b.avgDressedLbs)) || 0),
    0,
  );
  const totalLbsWhole = procBrThisYear.reduce((s, b) => s + (parseFloat(String(b.totalLbsWhole)) || 0), 0);
  const totalLbsCuts = procBrThisYear.reduce((s, b) => s + (parseFloat(String(b.totalLbsCuts)) || 0), 0);
  const wholePct =
    totalLbsWhole + totalLbsCuts > 0 ? Math.round((totalLbsWhole / (totalLbsWhole + totalLbsCuts)) * 100) : null;
  const cutsPct = wholePct != null ? 100 - wholePct : null;
  const totalFeedCost = finBr.reduce((s, b) => s + batchFeed(b) * (b.perLbStandardCost || 0), 0);
  const totalAllCost = finBr.reduce(
    (s, b) =>
      s + batchFeed(b) * (b.perLbStandardCost || 0) + (b.processingCost || 0) + (parseFloat(String(b.chickCost)) || 0),
    0,
  );
  const feedCostPct = totalAllCost > 0 ? Math.round((totalFeedCost / totalAllCost) * 100) : null;
  const totalProc = finBr.reduce((s, b) => s + (b.totalToProcessor || 0), 0);

  // Lbs produced trend — last 9 processed batches, sorted oldest to newest
  // (oldest-left -> newest-right). procBr is already processed-only.
  const lbsTrend = [...procBr]
    .filter((b): b is BroilerBatchRecord & {processingDate: string} => Boolean(b.processingDate))
    .sort((a, b) => a.processingDate.localeCompare(b.processingDate))
    .slice(-9)
    .map((b) => ({
      name: b.name,
      date: b.processingDate,
      whole: parseFloat(String(b.totalLbsWhole)) || 0,
      cuts: parseFloat(String(b.totalLbsCuts)) || 0,
      total: (parseFloat(String(b.totalLbsWhole)) || 0) + (parseFloat(String(b.totalLbsCuts)) || 0),
    }))
    .filter((b) => b.total > 0);

  const recentDailys = [...broilerDailys].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);

  const NavBtn = ({label, v}: {label: string; v: string}) => <Button onClick={() => setView(v)}>{label}</Button>;
  // A bird count that still includes a batch's ordered birds (no day-1 count
  // entered yet): gray value + small gray 'est.' (constitution D06, Q50).
  const birdCount = (n: number, estimated: boolean): React.ReactNode =>
    estimated ? (
      <span style={{color: 'var(--ink-muted)'}} data-broiler-count-estimated="1">
        {n.toLocaleString()}
        <span style={{fontSize: 12, fontWeight: 600, marginLeft: 4}}>est.</span>
      </span>
    ) : (
      n.toLocaleString()
    );
  // Three states where there used to be one. Both inputs arrived with the boot
  // payload, so "empty" and "not loaded yet" were the same frame and
  // "unreachable" could not happen. A route-local read makes all three real,
  // and every tile on this page is a COUNT: rendering a pending or failed read
  // as "0 In Process Batches / 0 Birds on Farm" is the empty-farm lie this
  // migration is supposed to remove, not introduce. The Header renders in all
  // three branches so the global nav chrome never disappears.
  if (homeResource.loading) {
    return (
      <div style={{minHeight: '100vh', background: 'var(--bg-page)'}} data-broiler-home-loaded="false">
        <Header />
        <div
          data-broiler-home-loading
          style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}
        >
          Loading broiler dashboard…
        </div>
      </div>
    );
  }

  if (homeResource.error) {
    return (
      <div style={{minHeight: '100vh', background: 'var(--bg-page)'}} data-broiler-home-loaded="false">
        <Header />
        <div
          data-broiler-home-load-error
          style={{textAlign: 'center', padding: '3rem', color: 'var(--danger)', fontSize: 13}}
        >
          Could not load the broiler dashboard.{' '}
          <Button type="button" onClick={() => homeResource.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // The boot-release readiness marker. The three branches above and this one
  // are mutually exclusive returns, so the attribute is a literal per branch
  // rather than the single `loading || loadError` expression CattleHomeView and
  // SheepHomeView use on their one container. `"true"` means broiler.home.core
  // resolved and this dashboard is painting real counts.
  return (
    <div style={{minHeight: '100vh', background: 'var(--bg-page)'}} data-broiler-home-loaded="true">
      <Header />
      <div
        style={{
          padding: '1.25rem',
          maxWidth: 1180,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        <h1 className="poultry-page-title">Broilers</h1>
        {/* R15: one joined stat strip, not loose tiles. */}
        {(() => {
          const yrBirds = procBrThisYear.reduce((sum, b) => sum + (parseInt(String(b.totalToProcessor)) || 0), 0);
          const yrWhole = procBrThisYear.reduce((sum, b) => sum + (parseFloat(String(b.totalLbsWhole)) || 0), 0);
          const yrCuts = procBrThisYear.reduce((sum, b) => sum + (parseFloat(String(b.totalLbsCuts)) || 0), 0);
          const yrTotal = yrWhole + yrCuts;
          return (
            <StatStrip
              tiles={[
                {label: 'In Process', value: activeBr.length, unit: activeBr.length === 1 ? 'batch' : 'batches'},
                {
                  label: 'Birds on Farm',
                  value: broilerOnFarmCounts.estimated
                    ? birdCount(broilerOnFarmCounts.onFarmBirds, true)
                    : broilerOnFarmCounts.onFarmBirds.toLocaleString(),
                  // Table F / C16 / Q30: started birds live on this tile's gray
                  // line beside mortality, never as the live figure.
                  sub: [
                    broilerOnFarmCounts.startedBirds > 0
                      ? broilerOnFarmCounts.startedBirds.toLocaleString() + ' started'
                      : '',
                    broilerOnFarmCounts.mortality > 0
                      ? broilerOnFarmCounts.mortality.toLocaleString() + ' mortality'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' · '),
                },
                {
                  label: 'Processed ' + currentYear,
                  value: yrBirds > 0 ? yrBirds.toLocaleString() : null,
                  unit: yrBirds > 0 ? 'birds' : undefined,
                  sub: procBrThisYear.length + ' batch' + (procBrThisYear.length !== 1 ? 'es' : ''),
                },
                {
                  label: 'Total Lbs ' + currentYear,
                  value: yrTotal > 0 ? Math.round(yrTotal).toLocaleString() : null,
                  unit: yrTotal > 0 ? 'lbs' : undefined,
                  sub:
                    yrWhole > 0
                      ? 'Whole ' +
                        Math.round(yrWhole).toLocaleString() +
                        ' · Cuts ' +
                        Math.round(yrCuts).toLocaleString()
                      : '',
                },
              ]}
            />
          );
        })()}

        {/* Active batch tracker — R06: the batch's own group color carries it. */}
        {activeBatchDetails.length > 0 && (
          <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between'}}>
              <div style={{fontSize: 18, fontWeight: 800, letterSpacing: '-.01em'}}>Active Batches</div>
              <a
                href="/broiler/batches"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/broiler/batches');
                }}
                style={{fontSize: 13.5, fontWeight: 700}}
              >
                All batches ›
              </a>
            </div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(270px,1fr))', gap: 14}}>
              {activeBatchDetails.map(({b, inBrooder, daysToProc, daysActive, mortSoFar, mortPct}) => {
                const groupHue = getBatchDisplayColor(b).bg;
                const tint = (pct: number) => 'color-mix(in srgb, ' + groupHue + ' ' + pct + '%, var(--surface))';
                const stats = calcBroilerStatsFromDailys(b, broilerDailys);
                const projFeed = calcBatchFeed(b);
                const nearProcessing = daysToProc != null && daysToProc <= 7;
                const weighIn = worstBroilerWeighInFlag(b, todayCentralISO());
                const tl = calcTimeline(b.hatchDate, b.breed, b.processingDate, b.growoutWeeks, b.brooderDays);
                const phaseOutISO = tl ? (inBrooder ? tl.brooderOut : tl.schoonerOut) : null;
                const phaseOut = phaseOutISO ? fmtS(phaseOutISO) : null;
                const onPill = {
                  padding: '4px 10px',
                  borderRadius: 'var(--r-pill)',
                  background: tint(15),
                  color: groupHue,
                  whiteSpace: 'nowrap' as const,
                };
                const offPill = {
                  padding: '4px 10px',
                  borderRadius: 'var(--r-pill)',
                  background: 'var(--surface-2)',
                  color: 'var(--label)',
                  whiteSpace: 'nowrap' as const,
                };
                const facts: {l: string; v: React.ReactNode; tone?: 'danger'}[] = [
                  {
                    l: 'To processing',
                    v: daysToProc != null ? Math.floor(daysToProc / 7) + 'w ' + (daysToProc % 7) + 'd' : '\u2014',
                    tone: nearProcessing ? 'danger' : undefined,
                  },
                  {l: 'On-farm birds', v: birdCount(stats.projectedBirds, stats.startedEstimated)},
                  {
                    l: 'Mortality',
                    v:
                      mortSoFar > 0 ? mortSoFar.toLocaleString() + (mortPct != null ? ' (' + mortPct + '%)' : '') : '0',
                    tone: mortSoFar > 15 ? 'danger' : undefined,
                  },
                  {
                    l: 'Feed actual / proj.',
                    v:
                      Math.round(stats.starterFeed + stats.growerFeed).toLocaleString() +
                      ' / ' +
                      Math.round(projFeed.starter + projFeed.grower).toLocaleString() +
                      ' lb',
                  },
                ];
                return (
                  <div
                    key={b.id}
                    {...openableProps(() => openBroilerBatch(b))}
                    className="hoverable-tile"
                    style={{
                      background: tint(6),
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--r-card)',
                      boxShadow: 'var(--shadow-card)',
                      padding: '18px 20px',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap'}}>
                      <GroupDot color={groupHue} />
                      <div style={{fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap'}}>{b.name}</div>
                      <div style={{fontSize: 13, fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap'}}>
                        {b.breed}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        marginTop: 12,
                        fontSize: 12.5,
                        fontWeight: 700,
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* WK4 / WK6 weigh-in, one worst-of badge (Q15, Q10, Table D). */}
                      {weighIn ? (
                        <Badge variant={weighIn.tone}>
                          <span data-broiler-weigh-in-flag={weighIn.state}>{weighIn.label}</span>
                        </Badge>
                      ) : null}
                      <span style={inBrooder ? onPill : offPill}>
                        {inBrooder && phaseOut ? 'Brooder · out ' + phaseOut : 'Brooder'}
                      </span>
                      <span style={inBrooder ? offPill : onPill}>
                        {!inBrooder && phaseOut ? 'Schooner · out ' + phaseOut : 'Schooner'}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        rowGap: 7,
                        columnGap: 12,
                        marginTop: 14,
                        fontSize: 13.5,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {facts.map(({l, v, tone}) => (
                        <React.Fragment key={l}>
                          <div style={{color: 'var(--text-2)'}}>{l}</div>
                          <div
                            style={{
                              fontWeight: 700,
                              textAlign: 'right',
                              color: tone === 'danger' ? 'var(--danger-ink)' : 'var(--ink)',
                              background: tone === 'danger' ? 'var(--danger-soft)' : undefined,
                              borderRadius: tone === 'danger' ? 6 : undefined,
                              padding: tone === 'danger' ? '0 6px' : undefined,
                              margin: tone === 'danger' ? '0 -6px' : undefined,
                            }}
                          >
                            {v}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                    <div style={{fontSize: 12.5, color: 'var(--text-2)', marginTop: 12}}>
                      Schooner {b.schooner} · {Math.floor(daysActive / 7)}w {daysActive % 7}d on farm
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* R: hatchery comparison replaces the breed comparison. */}
        {hatcheryRows.length > 0 && (
          <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <div style={{fontSize: 18, fontWeight: 800, letterSpacing: '-.01em'}}>Hatchery Comparison</div>
            <div
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-card)',
                boxShadow: 'var(--shadow-card)',
                overflow: 'hidden',
              }}
            >
              <table style={{width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums'}}>
                <thead>
                  <tr>
                    {['Hatchery', 'Batches', 'Avg mortality'].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          textAlign: i === 0 ? 'left' : 'right',
                          padding: '12px 18px',
                          fontSize: 11.5,
                          fontWeight: 700,
                          letterSpacing: '.06em',
                          textTransform: 'uppercase',
                          color: 'var(--label)',
                          borderBottom: '1px solid var(--hairline)',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hatcheryRows.map((row, i) => (
                    <tr key={row.hatchery} data-hatchery-row={row.hatchery}>
                      <td
                        style={{
                          padding: '12px 18px',
                          fontWeight: 700,
                          borderTop: i === 0 ? undefined : '1px solid var(--hairline)',
                        }}
                      >
                        {row.hatchery}
                        {row.best ? (
                          <Badge variant="ok" style={{marginLeft: 8}}>
                            Best
                          </Badge>
                        ) : null}
                        {row.high ? (
                          <Badge variant="warn" style={{marginLeft: 8}}>
                            High
                          </Badge>
                        ) : null}
                      </td>
                      <td
                        style={{
                          padding: '12px 18px',
                          textAlign: 'right',
                          color: 'var(--text-2)',
                          borderTop: i === 0 ? undefined : '1px solid var(--hairline)',
                        }}
                      >
                        {row.batches}
                      </td>
                      <td
                        style={{
                          padding: '12px 18px',
                          textAlign: 'right',
                          fontWeight: 700,
                          borderTop: i === 0 ? undefined : '1px solid var(--hairline)',
                        }}
                      >
                        {row.avgMortPct != null ? row.avgMortPct.toFixed(1) + '%' : '\u2014'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Performance trends */}
        {trend10.length >= 3 && (
          <div>
            <div
              style={{fontSize: 13, fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 8, letterSpacing: 0.3}}
            >
              Performance Trends — Last {trend10.length} Complete Batches
            </div>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12}}>
              <div
                style={{background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px'}}
              >
                <div style={{fontSize: 12, fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 10}}>
                  Mortality %
                </div>
                {trend10.map((b) => {
                  const m = Number(b.birdCount) > 0 ? (batchMort(b) / Number(b.birdCount)) * 100 : 0;
                  const maxM = Math.max(
                    ...trend10.map((b2) =>
                      Number(b2.birdCount) > 0 ? (batchMort(b2) / Number(b2.birdCount)) * 100 : 0,
                    ),
                    1,
                  );
                  return (
                    <div key={b.id}>
                      <div style={{fontSize: 10, color: 'var(--ink-muted)', marginBottom: 1}}>{b.name}</div>
                      {/* D02/§4: per-item comparisons are neutral bars — the old
                          always-red fill broke D05 (color without a state). */}
                      {trendBar(m, maxM, 'var(--text-secondary)')}
                    </div>
                  );
                })}
              </div>
              <div
                style={{background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px'}}
              >
                <div style={{fontSize: 12, fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 10}}>
                  Feed / Bird (lbs)
                </div>
                {trend10
                  .filter((b) => batchFeed(b) > 0 && Number(b.totalToProcessor) > 0)
                  .map((b) => {
                    const f = batchFeed(b) / (b.totalToProcessor || 1);
                    const maxF = Math.max(
                      ...trend10
                        .filter((b2) => batchFeed(b2) > 0 && Number(b2.totalToProcessor) > 0)
                        .map((b2) => batchFeed(b2) / (b2.totalToProcessor || 1)),
                      1,
                    );
                    return (
                      <div key={b.id}>
                        <div style={{fontSize: 10, color: 'var(--ink-muted)', marginBottom: 1}}>{b.name}</div>
                        {/* D02/§4: neutral per-item bar, value already labeled. */}
                        {trendBar(f, maxF, 'var(--text-secondary)')}
                      </div>
                    );
                  })}
              </div>
              <div
                style={{background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px'}}
              >
                <div style={{fontSize: 12, fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 10}}>
                  Avg Dressed Wt (lbs)
                </div>
                {trend10
                  .filter((b) => parseFloat(String(b.avgDressedLbs)) > 0)
                  .map((b) => {
                    const maxW = Math.max(
                      ...trend10
                        .filter((b2) => parseFloat(String(b2.avgDressedLbs)) > 0)
                        .map((b2) => parseFloat(String(b2.avgDressedLbs)) || 0),
                      1,
                    );
                    return (
                      <div key={b.id}>
                        <div style={{fontSize: 10, color: 'var(--ink-muted)', marginBottom: 1}}>{b.name}</div>
                        {/* D02/§4: neutral per-item bar, value already labeled. */}
                        {trendBar(parseFloat(String(b.avgDressedLbs)) || 0, maxW, 'var(--text-secondary)')}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* R16: cost reads as an equation, with cost per bird inside the total. */}
        {finBr.length >= 2 && (
          <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <div style={{fontSize: 18, fontWeight: 800, letterSpacing: '-.01em'}}>{'Cost ' + currentYear}</div>
            {(() => {
              const processingCost = finBr.reduce(
                (sum, batch) => sum + (parseFloat(String(batch.processingCost)) || 0),
                0,
              );
              const chickCost = finBr.reduce((sum, batch) => sum + (parseFloat(String(batch.chickCost)) || 0), 0);
              const money = (n: number) => (n > 0 ? '$' + Math.round(n).toLocaleString() : null);
              const feedShare =
                totalAllCost > 0 && totalFeedCost > 0
                  ? Math.round((totalFeedCost / totalAllCost) * 100) + '% of total'
                  : '';
              return (
                <EquationStrip
                  terms={[
                    {label: 'Feed', value: money(totalFeedCost), sub: feedShare},
                    {label: 'Processing', value: money(processingCost), operator: '+'},
                    {label: 'Chicks', value: money(chickCost), operator: '+'},
                    {
                      label: 'Total',
                      value: money(totalAllCost),
                      tone: 'data',
                      operator: '=',
                      sub:
                        totalProc > 0 && totalAllCost > 0
                          ? '$' + (totalAllCost / totalProc).toFixed(2) + ' / bird'
                          : '',
                    },
                  ]}
                />
              );
            })()}
          </div>
        )}

        {/* Lbs produced trend */}
        {lbsTrend.length >= 2 && (
          <div>
            <div
              style={{fontSize: 13, fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 8, letterSpacing: 0.3}}
            >
              Lbs Produced Trend — Last {lbsTrend.length} Batches
            </div>
            <div style={{background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '16px'}}>
              {(() => {
                const maxTotal = Math.max(...lbsTrend.map((b) => b.total), 1);
                return lbsTrend.map((b, i) => (
                  <div key={i} style={{marginBottom: 10}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3}}>
                      <span style={{color: 'var(--ink)', fontWeight: 600}}>{b.name}</span>
                      {/* D02: values in gray text, never white-on-fill; the
                          total stays the row's strong number. */}
                      <span style={{color: 'var(--ink-muted)'}}>
                        {fmt(b.date)} · {Math.round(b.whole).toLocaleString()} whole ·{' '}
                        {Math.round(b.cuts).toLocaleString()} cuts ·{' '}
                        <strong style={{color: 'var(--ink)'}}>{Math.round(b.total).toLocaleString()} lbs total</strong>
                      </span>
                    </div>
                    {/* D02 two-series stacked bar: whole (teal, series 1) +
                        cuts (plum, series 2) from the Table H palette — the
                        old ad-hoc green/blue were a status hue and an off-
                        palette blue. The 2px white gap is the shared surface
                        spacer between adjacent fills. */}
                    <div
                      style={{
                        display: 'flex',
                        height: 14,
                        borderRadius: 7 /* radius-allow: 14px stacked-bar track */,
                        overflow: 'hidden',
                        background: 'var(--divider)',
                      }}
                    >
                      {b.whole > 0 && (
                        <div
                          style={{
                            width: `${(b.whole / maxTotal) * 100}%`,
                            flexShrink: 0,
                            background: '#1F7A8C',
                            marginRight: b.cuts > 0 ? 2 : 0,
                            borderRadius: b.cuts > 0 ? '7px 0 0 7px' : 7,
                          }}
                        />
                      )}
                      {b.cuts > 0 && (
                        <div
                          style={{
                            width: `${(b.cuts / maxTotal) * 100}%`,
                            flexShrink: 0,
                            background: '#7A4FA0',
                            borderRadius: '0 7px 7px 0',
                          }}
                        />
                      )}
                    </div>
                  </div>
                ));
              })()}
              {/* D02: legend chips under the chart, labels in gray ink. */}
              <div style={{display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: 'var(--ink-muted)'}}>
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: '#1F7A8C',
                      borderRadius: 2 /* radius-allow: 10px legend swatch */,
                      marginRight: 4,
                    }}
                  />
                  Whole Birds
                </span>
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: '#7A4FA0',
                      borderRadius: 2 /* radius-allow: 10px legend swatch */,
                      marginRight: 4,
                    }}
                  />
                  Cuts (excl. necks, feet, backs)
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
